import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Gauge, Plus, ShieldX } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Skeleton, Tabs, TabsList, TabsTrigger } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useServiceWebAccessControlRules,
  useServiceRateLimitRules,
  useServiceWaapEnabledSites,
  useDeleteServiceWebAccessControlRule,
  useDeleteServiceRateLimitRule,
  type WebAccessControlRuleListItem,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { useWaapUpgradeGate } from '@/pages/services/waap/use-waap-upgrade-gate';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './ProgrammableMitigationTab.module.css';

const muted = (v: React.ReactNode) => <span className={styles.muted}>{v}</span>;

/**
 * Programmable Mitigation — account/product-level rule engine (vendor PBR
 * strategies). Two tabs mirror the portal: Web Access Control (allow/block rules)
 * and Rate Limiting. Rules span every website and apply to a selected set
 * ("Apply to"), so this is account-scoped — there is no per-website picker. The
 * BE endpoint is domain-scoped (any domainId resolves the account's customer), so
 * we pass a representative website from the WAAP website context.
 */
export function ProgrammableMitigationTab() {
  const [tab, setTab] = useState('web-access-control');

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'Programmable Mitigation' }]} />}
        title="Programmable Mitigation"
        description="Programmable mitigation inspects requests against custom conditions and applies actions such as blocking or rate limiting at the edge."
      />

      <Tabs value={tab} onValueChange={setTab} style={{ marginBottom: 'var(--vn-space-4)' }}>
        <TabsList>
          <TabsTrigger value="web-access-control">Web Access Control</TabsTrigger>
          <TabsTrigger value="rate-limiting">Rate Limiting</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'web-access-control' ? <WebAccessControlList /> : <RateLimitingList />}
    </Container>
  );
}

/** The Web Access Control rules list (the first PBR tab). */
function WebAccessControlList() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const { gate, upgradeDialog } = useWaapUpgradeGate();
  const base = `/services/waap/${serviceId}`;
  const [pendingDelete, setPendingDelete] = useState<WebAccessControlRuleListItem | null>(null);

  const { data: rules = [], isLoading } = useServiceWebAccessControlRules(serviceId);
  const { data: sites = [] } = useServiceWaapEnabledSites(serviceId);

  // Resolve site IDs → domain names for the Website column.
  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of sites) m.set(s.id, s.domain);
    return (id: number) => m.get(id) ?? `#${id}`;
  }, [sites]);

  const del = useDeleteServiceWebAccessControlRule(serviceId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'pbr_web_access_control_rule', 'delete');
      show({ tone: 'success', title: 'Web access control rule deleted' });
      setPendingDelete(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Delete failed', description: errMessage(e) }),
  });

  const columns = useMemo<ColumnDef<WebAccessControlRuleListItem>[]>(
    () => [
      { accessorKey: 'id', header: 'ID', cell: ({ row }) => muted(row.original.id) },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => <span className={styles.name}>{row.original.name}</span>,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => row.original.description || muted('—'),
      },
      {
        id: 'website',
        header: 'Website',
        cell: ({ row }) => {
          const ids = row.original.websites ?? [];
          return ids.length ? ids.map(siteName).join(', ') : muted('—');
        },
      },
      {
        id: 'actions',
        header: 'Operation',
        cell: ({ row }) => (
          <div className={styles.actions}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                navigate(`${base}/programmable-mitigation/web-access-control/${row.original.id}`)
              }
            >
              Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setPendingDelete(row.original)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [siteName, navigate, base],
  );

  const addButton = (
    <Button
      startIcon={<Plus size={16} />}
      onClick={gate('programmableMitigation', 'Programmable Mitigation', () =>
        navigate(`${base}/programmable-mitigation/web-access-control/new`),
      )}
    >
      Add Web Access Control Rule
    </Button>
  );

  return (
    <Section
      title="Web Access Control"
      description="Web access control restricts requests with specific key values, such as IP address or user agent."
      actions={addButton}
    >
      {isLoading ? (
        <div className={styles.skeletonList}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className={styles.skeletonRow} />
          ))}
        </div>
      ) : (
        <div className={styles.tableScroll}>
          <DataTable
            columns={columns}
            data={rules}
            emptyState={
              <EmptyState
                size="sm"
                icon={<ShieldX size={20} />}
                title="No web access control rules"
                description="Add a rule to restrict requests by IP, path or other request signals."
              />
            }
          />
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete web access control rule?"
        description={`Delete "${pendingDelete?.name}"? This removes the rule from the websites it applies to. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => pendingDelete && del.mutate(pendingDelete.id)}
      />
      {upgradeDialog}
    </Section>
  );
}

/** The Rate Limiting rules list (the second PBR tab). Mirrors the Web Access
 *  Control list against the rate-limit hooks/routes. */
function RateLimitingList() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const { gate, upgradeDialog } = useWaapUpgradeGate();
  const base = `/services/waap/${serviceId}`;
  const [pendingDelete, setPendingDelete] = useState<WebAccessControlRuleListItem | null>(null);

  const { data: rules = [], isLoading } = useServiceRateLimitRules(serviceId);
  const { data: sites = [] } = useServiceWaapEnabledSites(serviceId);

  // Resolve site IDs → domain names for the Website column.
  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of sites) m.set(s.id, s.domain);
    return (id: number) => m.get(id) ?? `#${id}`;
  }, [sites]);

  const del = useDeleteServiceRateLimitRule(serviceId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'pbr_rate_limit_rule', 'delete');
      show({ tone: 'success', title: 'Rate limiting rule deleted' });
      setPendingDelete(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Delete failed', description: errMessage(e) }),
  });

  const columns = useMemo<ColumnDef<WebAccessControlRuleListItem>[]>(
    () => [
      { accessorKey: 'id', header: 'ID', cell: ({ row }) => muted(row.original.id) },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => <span className={styles.name}>{row.original.name}</span>,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => row.original.description || muted('—'),
      },
      {
        id: 'website',
        header: 'Website',
        cell: ({ row }) => {
          const ids = row.original.websites ?? [];
          return ids.length ? ids.map(siteName).join(', ') : muted('—');
        },
      },
      {
        id: 'actions',
        header: 'Operation',
        cell: ({ row }) => (
          <div className={styles.actions}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                navigate(`${base}/programmable-mitigation/rate-limiting/${row.original.id}`)
              }
            >
              Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setPendingDelete(row.original)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [siteName, navigate, base],
  );

  const addButton = (
    <Button
      startIcon={<Plus size={16} />}
      onClick={gate('programmableMitigation', 'Programmable Mitigation', () =>
        navigate(`${base}/programmable-mitigation/rate-limiting/new`),
      )}
    >
      Add Rate Limiting Rule
    </Button>
  );

  return (
    <Section
      title="Rate Limiting"
      description="Rate limiting restricts requests that excessively access the website."
      actions={addButton}
    >
      {isLoading ? (
        <div className={styles.skeletonList}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className={styles.skeletonRow} />
          ))}
        </div>
      ) : (
        <div className={styles.tableScroll}>
          <DataTable
            columns={columns}
            data={rules}
            emptyState={
              <EmptyState
                size="sm"
                icon={<Gauge size={20} />}
                title="No rate limiting rules"
                description="Add a rule to throttle requests that excessively access a website."
              />
            }
          />
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete rate limiting rule?"
        description={`Delete "${pendingDelete?.name}"? This removes the rule from the websites it applies to. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => pendingDelete && del.mutate(pendingDelete.id)}
      />
      {upgradeDialog}
    </Section>
  );
}
